// Helper utilities for building brand-specific URLs in product filters
// eslint-disable-next-line import/prefer-default-export
export class ProductsFilterBrandURLHelper {
  // Generate slug from brand name matching Ruby's Spree::Brand.generate_slug algorithm
  // Must match exactly to avoid 404s when navigating to brand pages
  // Ruby version: name.downcase.gsub(/[^\w\s-]/, '').gsub(/\s+/, '-').squeeze('-').gsub(/^-|-$/, '')
  //
  // Note: This does NOT handle collision resolution (e.g., appending -1, -2 for duplicate slugs).
  // Server-side collision handling is performed during brand creation and stored in the database.
  // The client-side code only needs to generate the base slug to construct URLs, which the server
  // will resolve to the correct brand record.
  //
  // @param name [String] Brand name (e.g., "Bella + Canvas", "M&O")
  // @return [String] SEO-friendly slug (e.g., "bella-canvas", "mo")
  static generateSlug(name) {
    if (!name) return "";

    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove non-word, non-space, non-hyphen chars (\w = a-zA-Z0-9_)
      .replace(/\s+/g, "-") // Convert spaces to hyphens
      .replace(/-+/g, "-") // Collapse multiple consecutive hyphens (Ruby's squeeze('-'))
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  }

  // Check if URL is a brand page (brand all-products or brand category)
  static isBrandPageURL(url) {
    const brandCategoryMapping = window.jiffy?.brandCategoryMapping || {};
    const categorySlugs = Object.values(brandCategoryMapping);

    // Check if the base URL is a brand category URL
    if (categorySlugs.length > 0) {
      const slugsPattern = categorySlugs.join("|");
      const brandCategoryPattern = new RegExp(`^/(?:ca/)?brands/[^/]+/(${slugsPattern})$`);
      if (url.pathname.match(brandCategoryPattern)) {
        return true;
      }
    }

    // Check if we're on a brand all-products page
    const brandAllProductsPattern = /^\/(?:ca\/)?brands\/[^/]+\/all-products$/;
    return url.pathname.match(brandAllProductsPattern);
  }

  // Check if a brand name matches the current brand page slug
  static isCurrentBrandPage(brandName) {
    const currentURL = new URL(window.location.href);
    const brandSlug = currentURL.pathname.match(/\/brands\/([^/]+)/)?.[1];
    if (!brandSlug) return false;

    const normalizedBrandName = ProductsFilterBrandURLHelper.generateSlug(brandName);
    return brandSlug === normalizedBrandName;
  }

  // Get category slug from category name using server-side mapping
  static getCategorySlug(categoryName) {
    const mapping = window.jiffy?.brandCategoryMapping || {};
    return mapping[categoryName] || null;
  }

  // Get category name from category slug using server-side mapping (reverse lookup)
  static getCategoryNameFromSlug(categorySlug) {
    if (!categorySlug) return null;

    const mapping = window.jiffy?.brandCategoryMapping || {};
    const match = Object.entries(mapping).find(([, slug]) => slug === categorySlug);
    return match ? match[0] : null;
  }

  // Convert query string URL to brand page URL
  static convertToBrandPageURL(queryStringURL, checkedFilters) {
    const brandFilters = checkedFilters.brand || [];
    if (brandFilters.length !== 1) return queryStringURL;

    const brandSlug = ProductsFilterBrandURLHelper.generateSlug(brandFilters[0]);

    const categoryFilters = checkedFilters.category || [];
    const styleFilters = checkedFilters.style || [];

    const currentURL = new URL(window.location.href);
    const storePrefix = currentURL.pathname.startsWith("/ca/") ? "/ca" : "";

    const url = new URL(queryStringURL);
    url.searchParams.delete("brand");
    url.searchParams.delete("category");
    url.searchParams.delete("style");

    const brandURL = ProductsFilterBrandURLHelper.buildBrandURLPath({
      brandSlug,
      storePrefix,
      categoryFilters,
      styleFilters,
      url
    });

    return url.search ? `${brandURL}${url.search}` : brandURL;
  }

  // Build brand URL path based on categories
  static buildBrandURLPath({ brandSlug, storePrefix, categoryFilters, styleFilters, url }) {
    const totalFiltersCount = categoryFilters.length + styleFilters.length;

    if (totalFiltersCount === 1) {
      const singleFilterValue = categoryFilters[0] || styleFilters[0];
      const categorySlug = ProductsFilterBrandURLHelper.getCategorySlug(singleFilterValue);
      return categorySlug
        ? `${storePrefix}/brands/${brandSlug}/${categorySlug}`
        : `${storePrefix}/brands/${brandSlug}/all-products`;
    }

    if (totalFiltersCount === 0) {
      return `${storePrefix}/brands/${brandSlug}/all-products`;
    }

    categoryFilters.forEach(category => url.searchParams.append("category", category));
    styleFilters.forEach(style => url.searchParams.append("style", style));
    return `${storePrefix}/brands/${brandSlug}/all-products`;
  }
}
